/**
 * Moss Circuit Scene — ancient forest of massive gnarled trees covered in life,
 * thick vine curtains dripping with bioluminescence, creatures everywhere,
 * dramatic explosion reactivity. A living, breathing organism.
 */

import { Graphics } from 'pixi.js';
import type { StageScene, ParallaxLayers } from '../stage-scene';
import type { StageTheme } from '../../../stages';
import { WORLD } from '../../../constants';
import { FallingParticle } from '../particles/falling-particle';
import { buildTrees, buildFernsAndGroundCover, buildVines, TreeNode, Vine } from '../builders/moss-builder';

interface GlowBug { gfx: Graphics; x: number; y: number; targetX: number; targetY: number; blinkPhase: number; moveTimer: number; tier: number }

export class MossCircuitScene implements StageScene {
  private vines: Vine[] = [];
  private glowBugs: GlowBug[] = [];
  private spores: FallingParticle[] = [];
  private treeNodes: TreeNode[] = [];
  private canopyGlow: Graphics[] = [];
  private layers: ParallaxLayers | null = null;

  build(layers: ParallaxLayers, _theme: StageTheme): void {
    this.layers = layers;
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // ── Sky Layer: Deep forest canopy ──

    const skyBg = new Graphics();
    skyBg.rect(0, 0, W, H);
    skyBg.fill({ color: 0x040c04, alpha: 1.0 });
    layers.sky.container.addChild(skyBg);

    // Canopy light filtering — 10 dappled patches from top to mid-height
    const lightPatches = [
      [W * 0.08, H * 0.04, 160], [W * 0.2, H * 0.05, 180], [W * 0.35, H * 0.12, 120],
      [W * 0.45, H * 0.02, 220], [W * 0.55, H * 0.18, 100], [W * 0.6, H * 0.1, 150],
      [W * 0.7, H * 0.06, 160], [W * 0.78, H * 0.15, 110], [W * 0.88, H * 0.08, 130],
      [W * 0.9, H * 0.03, 140],
    ];
    for (const [lx, ly, lr] of lightPatches) {
      const patch = new Graphics();
      patch.ellipse(lx, ly, lr, lr * 0.5);
      patch.fill({ color: 0x114411, alpha: 0.1 });
      layers.sky.container.addChild(patch);
      this.canopyGlow.push(patch);
    }

    // ── Deep Layer: Massive ancient trees ──

    this.treeNodes = buildTrees(layers, W, H);
    buildFernsAndGroundCover(layers, W, H);

    // ── Mid Layer: Vine curtains, glow bugs, spores ──

    this.vines = buildVines(layers, W, H);
    this.buildGlowBugs(layers, W, H);
    this.buildSpores(layers, W, H);
  }

  // ── Glow bugs — 3 tiers ──

  private buildGlowBugs(layers: ParallaxLayers, W: number, H: number): void {
    // Tier 0: Fireflies (25) — standard
    for (let i = 0; i < 25; i++) {
      this.createBug(layers, W, H, 0, 8, 2.5, 0x88ffaa, 0xccffdd, 0.06, 0.5,
        H * 0.1, H * 0.75);
    }
    // Tier 1: Large moths (10) — bigger, warmer
    for (let i = 0; i < 10; i++) {
      this.createBug(layers, W, H, 1, 14, 4, 0xaaffcc, 0xddffee, 0.08, 0.55,
        H * 0.1, H * 0.7);
    }
    // Tier 2: Canopy motes (5) — tiny, fast blink, confined to top 35%
    for (let i = 0; i < 5; i++) {
      this.createBug(layers, W, H, 2, 4, 1, 0x88ffaa, 0xccffdd, 0.04, 0.4,
        H * 0.02, H * 0.35);
    }
  }

  private createBug(
    layers: ParallaxLayers, W: number, _H: number, tier: number,
    haloR: number, coreR: number, haloColor: number, coreColor: number,
    haloAlpha: number, coreAlpha: number, minY: number, maxY: number,
  ): void {
    const gfx = new Graphics();
    const x = Math.random() * W;
    const y = minY + Math.random() * (maxY - minY);
    gfx.circle(0, 0, haloR);
    gfx.fill({ color: haloColor, alpha: haloAlpha });
    gfx.circle(0, 0, coreR);
    gfx.fill({ color: coreColor, alpha: coreAlpha });
    gfx.x = x;
    gfx.y = y;
    layers.mid.container.addChild(gfx);

    const wanderRadius = tier === 1 ? 120 : tier === 2 ? 50 : 80;
    this.glowBugs.push({
      gfx, x, y,
      targetX: x + (Math.random() - 0.5) * wanderRadius,
      targetY: y + (Math.random() - 0.5) * wanderRadius * 0.6,
      blinkPhase: Math.random() * Math.PI * 2,
      moveTimer: Math.random() * 3,
      tier,
    });
  }

  // ── Spores — bidirectional ──

  private buildSpores(layers: ParallaxLayers, W: number, H: number): void {
    // 60 upward
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H;
      const size = 0.6 + Math.random() * 0.6;
      const speed = 6 + Math.random() * 10;

      const spore = new FallingParticle(layers.mid.container, {
        x, y, size,
        color: 0x88ffaa,
        alpha: 0.12 + Math.random() * 0.12,
        speed: -speed, // Negative for upward
        drift: 0, // Sway handled manually in update
      });
      (spore as any).baseSpeed = speed;
      (spore as any).direction = -1;
      this.spores.push(spore);
    }
    // 20 downward (heavier pollen from canopy)
    for (let i = 0; i < 20; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H * 0.5;
      const size = 0.6 + Math.random() * 0.5;
      const speed = 3 + Math.random() * 3;

      const spore = new FallingParticle(layers.mid.container, {
        x, y, size,
        color: 0xaaffbb,
        alpha: 0.10 + Math.random() * 0.10,
        speed: speed, // Positive for downward
        drift: 0, // Sway handled manually in update
      });
      (spore as any).baseSpeed = speed;
      (spore as any).direction = 1;
      this.spores.push(spore);
    }
  }

  // ── Per-frame update ──

  update(dt: number, elapsed: number): void {
    const W = WORLD.VIRTUAL_WIDTH;
    const H = WORLD.VIRTUAL_HEIGHT;

    // Canopy light shimmer
    for (let i = 0; i < this.canopyGlow.length; i++) {
      this.canopyGlow[i].alpha = 0.08 + 0.04 * Math.sin(elapsed * 0.5 + i * 1.5);
    }

    // Tree glow node pulse — base sin wave + explosion brightness decay
    for (const node of this.treeNodes) {
      node.brightness *= Math.pow(0.92, dt * 60);
      const basePulse = 0.8 + 0.2 * Math.sin(elapsed * 1.8 + node.phase);
      node.gfx.alpha = Math.min(1, basePulse + node.brightness);
    }

    // Vine sway — per-vine phase offset, amplitude scaled by length
    for (const vine of this.vines) {
      vine.sway *= Math.pow(0.94, dt * 60);
      const amplitude = 2.5 * (vine.length / 300);
      vine.gfx.x = vine.baseX + Math.sin(elapsed * 0.8 + vine.phase) * amplitude + vine.sway;
    }

    // Glow bugs — wander + blink by tier
    for (const bug of this.glowBugs) {
      bug.moveTimer -= dt;
      if (bug.moveTimer <= 0) {
        const wanderR = bug.tier === 1 ? 120 : bug.tier === 2 ? 50 : 100;
        bug.targetX = bug.x + (Math.random() - 0.5) * wanderR;
        bug.targetY = bug.y + (Math.random() - 0.5) * wanderR * 0.6;
        bug.targetX = Math.max(0, Math.min(W, bug.targetX));
        const minY = bug.tier === 2 ? H * 0.02 : H * 0.05;
        const maxY = bug.tier === 2 ? H * 0.35 : H * 0.75;
        bug.targetY = Math.max(minY, Math.min(maxY, bug.targetY));
        bug.moveTimer = bug.tier === 1 ? 2 + Math.random() * 4 : 1.5 + Math.random() * 3.5;
      }
      const lerpRate = bug.tier === 1 ? 0.4 : 0.6;
      bug.x += (bug.targetX - bug.x) * dt * lerpRate;
      bug.y += (bug.targetY - bug.y) * dt * lerpRate;
      bug.gfx.x = bug.x;
      bug.gfx.y = bug.y;
      // Blinking — tier 2 (canopy motes) blink faster
      const blinkSpeed = bug.tier === 2 ? 4.0 : 2.5;
      const blink = Math.sin(elapsed * blinkSpeed + bug.blinkPhase);
      bug.gfx.alpha = blink > 0 ? 0.3 + blink * 0.7 : 0.15;
    }

    // Spores — drift with gentle sway, bidirectional
    for (const spore of this.spores) {
      const dynSpore = spore as any;

      // We set spore.speed dynamically with sign already, but onExplosion changes it
      // so we use the stored direction for wrap logic
      spore.y += dynSpore.direction > 0 ? spore.speed * dt : -spore.speed * dt;

      // Wrap
      if (dynSpore.direction < 0 && spore.y < -20) {
        spore.y = H + 20;
        spore.x = Math.random() * W;
        spore.speed = dynSpore.baseSpeed;
      } else if (dynSpore.direction > 0 && spore.y > H + 20) {
        spore.y = -20;
        spore.x = Math.random() * W;
        spore.speed = dynSpore.baseSpeed;
      }

      // Downward spores get wider lateral sway
      const swayAmp = dynSpore.direction > 0 ? 10 : 5;
      spore.gfx.x = spore.x + Math.sin(elapsed * 1.2 + spore.driftPhase) * swayAmp;
      spore.gfx.y = spore.y;
      spore.gfx.alpha = spore.baseAlpha * (0.8 + 1.0 * Math.sin(elapsed * 1.5 + spore.driftPhase)); // Normalize around baseAlpha
    }
  }

  // ── Explosion reactivity ──

  onExplosion(worldX: number, worldY: number, radius: number): void {
    const radiusScale = Math.max(0.5, radius / 50);

    // Enhanced vine sway — Y-distance falloff + radius scaling + length-dependent amplitude
    for (const vine of this.vines) {
      const dx = worldX - vine.baseX;
      const dy = worldY - (vine.topY + vine.length * 0.5);
      const dist = Math.sqrt(dx * dx + dy * dy);
      const falloff = Math.max(0, 1 - dist / 800);
      const lengthAmp = vine.length / 300;
      vine.sway += Math.sign(-dx) * falloff * 18 * radiusScale * lengthAmp;
    }

    // Bug scatter — push nearby bugs away from blast
    for (const bug of this.glowBugs) {
      const dx = bug.x - worldX;
      const dy = bug.y - worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const falloff = Math.max(0, 1 - dist / 600);
      if (falloff > 0) {
        const pushStr = 150 * falloff * radiusScale;
        const angle = Math.atan2(dy, dx);
        bug.targetX = bug.x + Math.cos(angle) * pushStr;
        bug.targetY = bug.y + Math.sin(angle) * pushStr;
      }
    }

    // Spore burst — nearby spores get speed boost
    for (const spore of this.spores) {
      const dx = spore.x - worldX;
      const dy = spore.y - worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const falloff = Math.max(0, 1 - dist / 400);
      if (falloff > 0) {
        const dynSpore = spore as any;
        spore.speed = dynSpore.baseSpeed + dynSpore.baseSpeed * 2 * falloff * radiusScale;
      }
    }

    // Canopy flash — briefly brighten all canopy patches
    for (const patch of this.canopyGlow) {
      patch.alpha = Math.min(0.3, patch.alpha + 0.08 * radiusScale);
    }

    // Tree node pulse — nearby nodes get brightness boost
    for (const node of this.treeNodes) {
      const nx = node.gfx.x || 0;
      const ny = node.gfx.y || 0;
      const dx = nx - worldX;
      const dy = ny - worldY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const falloff = Math.max(0, 1 - dist / 500);
      if (falloff > 0) {
        node.brightness = Math.min(1, node.brightness + 0.6 * falloff * radiusScale);
      }
    }
  }

  destroy(): void {
    this.vines = [];
    this.glowBugs = [];
    this.spores = [];
    this.treeNodes = [];
    this.canopyGlow = [];
    if (this.layers) {
      this.layers.sky.clear();
      this.layers.deep.clear();
      this.layers.mid.clear();
    }
  }
}
